import type { Graph } from '@maxgraph/core';
import type { MaterialNodeSpec } from './types';
import { resolveMaterialLabel } from './labelGen';
import { generateId } from '../utils/id';
import { classify } from './classify';
import { CellNotFoundError, WrongCellTypeError } from './errors';
import { MATERIAL_DIMENSIONS, MATERIAL_STYLE, MATERIAL_REMAINDER_STYLE } from '../styles';

function buildMaterialValue(spec: MaterialNodeSpec, label: string) {
  return {
    kind: 'material' as const,
    label,
    id: spec.id,
    name: spec.name,
    isEmpty: spec.isEmpty ?? false,
    isReminder: spec.isReminder ?? false,
    createdAt: spec.createdAt ?? new Date().toISOString(),
  };
}

export function addMaterialNode(graph: Graph, spec: MaterialNodeSpec): string {
  const id = spec.id ?? generateId('material');
  const label = resolveMaterialLabel(spec);

  const value = buildMaterialValue({ ...spec, id }, label);
  const x = spec.position?.x ?? 0;
  const y = spec.position?.y ?? 0;
  const { width, height } = MATERIAL_DIMENSIONS;
  
  // Use remainder style for remainder nodes, standard style for regular materials
  const style = spec.isReminder ? MATERIAL_REMAINDER_STYLE : MATERIAL_STYLE;

  graph.batchUpdate(() => {
    graph.insertVertex({
      id,
      value,
      position: [x, y],
      size: [width, height],
      style,
    });
  });

  return id;
}

export function updateMaterialNode(
  graph: Graph,
  id: string,
  patch: Partial<MaterialNodeSpec>,
): void {
  const cell = graph.getDataModel().getCell(id);
  if (!cell) throw new CellNotFoundError(id);
  if (classify(cell) !== 'material') throw new WrongCellTypeError(id, 'material');

  const oldValue = cell.getValue();
  const merged = { ...oldValue, ...patch };

  // If name changed, update the display label
  if (patch.name !== undefined) {
    merged.label = patch.name;
  }

  graph.batchUpdate(() => {
    graph.getDataModel().setValue(cell, merged);
    
    // If isReminder changed, update the cell style
    if (patch.isReminder !== undefined) {
      const newStyle = patch.isReminder ? MATERIAL_REMAINDER_STYLE : MATERIAL_STYLE;
      cell.setStyle(newStyle);
    }
  });
}

export function removeMaterialNode(graph: Graph, id: string): void {
  const cell = graph.getDataModel().getCell(id);
  if (!cell) throw new CellNotFoundError(id);
  if (classify(cell) !== 'material') throw new WrongCellTypeError(id, 'material');

  graph.batchUpdate(() => {
    graph.removeCells([cell], true);
  });
}
